import React, { useState } from "react";
import TriloChatHelpPanel from "./TriloChatHelpPanel";
import { PlusIcon, TrashIcon } from "./TriloChatIcons";
import { formatRelativeDayLabel } from "../utils/FormatDate";
import { NO_PERMISSION_TITLE } from "../utils/permissions";
import {
  CHAT_MESSAGES,
  DEFAULT_SESSION_TITLE,
} from "../utils/triloChatConstants";

/**
 * TriloChatSessionsSidebar
 *
 * Conversation list down the left of MW, plus the "New chat" and help buttons.
 *
 * Props:
 * - sessions: Array of { session_id, title, updated_at }, newest first
 * - activeSessionId: Currently open session id, or null
 * - loadingSessions: Whether the list is still being fetched
 * - onSelectSession: (sessionId: string) => void
 * - onDeleteSession: (event, sessionId: string) => void
 * - onNewChat: () => void
 * - canCreate: mw:sessions:create - whether "New chat" is usable
 * - canDelete: mw:sessions:delete - whether the row delete icon is usable
 */
const TriloChatSessionsSidebar = ({
  sessions,
  activeSessionId,
  loadingSessions,
  onSelectSession,
  onDeleteSession,
  onNewChat,
  canCreate = true,
  canDelete = true,
}) => {
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="trilochat__sessions">
      <div className="trilochat__sessions-header">
        <h3>MW</h3>
        <div className="trilochat__sessions-header-actions">
          <button
            className="trilochat__help-btn"
            onClick={() => setShowHelp(true)}
            aria-label="Help">
            ?
          </button>
          <button
            className="trilochat__new-btn"
            onClick={onNewChat}
            disabled={!canCreate}
            title={canCreate ? undefined : NO_PERMISSION_TITLE}>
            <PlusIcon />
            New chat
          </button>
        </div>
      </div>

      {showHelp && <TriloChatHelpPanel onClose={() => setShowHelp(false)} />}

      <div className="trilochat__sessions-list">
        {loadingSessions ? null : sessions.length === 0 ? (
          <p className="trilochat__sessions-empty">
            {CHAT_MESSAGES.noSessions}
          </p>
        ) : (
          sessions.map(s => (
            <button
              key={s.session_id}
              className={`trilochat__session-item ${s.session_id === activeSessionId ? "trilochat__session-item--active" : ""}`}
              onClick={() => onSelectSession(s.session_id)}>
              <div className="trilochat__session-info">
                <div className="trilochat__session-title">
                  {s.title || DEFAULT_SESSION_TITLE}
                </div>
                <div className="trilochat__session-time">
                  {formatRelativeDayLabel(s.updated_at)}
                </div>
              </div>
              <span
                className={`trilochat__session-delete${canDelete ? "" : " trilochat__session-delete--disabled"}`}
                role="button"
                aria-label="Delete chat"
                aria-disabled={!canDelete}
                title={canDelete ? undefined : NO_PERMISSION_TITLE}
                onClick={e =>
                  canDelete
                    ? onDeleteSession(e, s.session_id)
                    : e.stopPropagation()
                }>
                <TrashIcon />
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default TriloChatSessionsSidebar;
